import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Stack,
  Paper,
  Typography,
  List,
  ListItemButton,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FilterSelect,
  SegmentedFilter,
  TextField,
} from '../../ui/core';
import { flushPageSx, surfaceCardSx as cardSx, cardHeaderSx, cardBodySx, scrollY, softFill, hairline } from '../../ui/styles';
import { StationIcon } from '../../ui/icons';
import useCities from '../../hooks/useCities';
import {
  fetchBroadcastPreview,
  sendBroadcast,
  resetBroadcast,
  fetchBroadcastHistory,
  recordLocalBroadcast,
  hydrateHistoryCache,
} from '../../redux/Notifications/actions';
import {
  availableDriverNotificationTemplates,
  TripDayOptions,
  DriverSegmentOptions,
  getDriverSegment,
  xlSegmentName,
} from '../../consts/driver-notifications';
import { useHasPermission } from '../../helpers/api/auth/index';
import useToast from '../../hooks/useToast';
import SentLog from './SentLog';

// Instant-paint cache for the sent-log: the last server response is kept in
// localStorage so the log shows on mount (and after a reload) before the live
// fetch resolves. The live fetch always reconciles to server truth.
const HISTORY_CACHE_KEY = 'hitch-notifications-sent-log';

const readHistoryCache = () => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_CACHE_KEY)) || [];
  } catch (e) {
    return [];
  }
};

const writeHistoryCache = (items) => {
  try {
    localStorage.setItem(HISTORY_CACHE_KEY, JSON.stringify(items));
  } catch (e) {
    /* private mode / quota — the log just won't pre-paint, not fatal */
  }
};

const cityLabel = (c) => (c ? `${c.cityName}, ${c.stateAbbr}` : '');

// "N drivers in City", worded for the chosen segment, for the recipient line
// and the confirm dialog.
const describeAudience = (count, segmentKey, city) => {
  const drivers = count === 1 ? 'driver' : 'drivers';
  if (segmentKey === 'xl') return `${count} XL ${drivers} in ${cityLabel(city)}`;
  if (segmentKey === 'all') return `${count} ${drivers} in ${cityLabel(city)} (XL and non-XL)`;
  return `${count} ${drivers} in ${cityLabel(city)} (excluding XL drivers)`;
};

// From/To sit side by side and hug their pills (a 2-col 1fr grid stretched them
// to opposite edges of the card). They wrap on narrow widths.
const routeFieldsSx = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
};

const routeFieldSx = { flex: '0 0 auto' };

// Quiet uppercase field heading — the same treatment PromotionForm's
// SectionLabel uses, so field groups across the redesigned admin read alike.
const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      mb: '10px',
    }}
  >
    {children}
  </Typography>
);

// Sub-nav row for a template — same shape as the Promotions code list (label
// + one-line description, a soft chip for a notable trait), so picking a
// template reads like picking a promo code rather than a plain dropdown.
const TemplateListItem = ({ template, selected, onClick }) => (
  <ListItemButton
    selected={selected}
    onClick={onClick}
    disableRipple
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '3px',
      py: '10px',
      px: '12px',
      mb: '1px',
      borderRadius: '10px',
      transition: 'background-color 0.12s ease',
      '&.Mui-selected': {
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.09)' : '#EEF0F2'),
        '&:hover': {
          backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.11)' : '#E8EAED'),
        },
      },
      '&:hover': {
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F6F7F8'),
      },
    }}
  >
    <Box display="flex" gap="8px" alignItems="center" width="100%">
      <Typography fontWeight={600} fontSize="14px" sx={{ flex: '0 1 auto', minWidth: 0 }}>
        {template.label.split(' (')[0]}
      </Typography>
      {template.fields.length > 0 && (
        <Typography
          component="span"
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            px: '6px',
            py: '1px',
            borderRadius: '5px',
            color: (theme) => theme.palette.text.secondary,
            backgroundColor: softFill,
            flexShrink: 0,
          }}
        >
          Needs route
        </Typography>
      )}
    </Box>
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12.5px' }}>
      {template.description}
    </Typography>
  </ListItemButton>
);

const DriverNotifications = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const canSend = useHasPermission('send_driver_notifications');
  // Full ADMIN only (the hook also passes ADMIN for any permission, so asking
  // for 'ADMIN' itself is true for ADMINs alone). Unlocks `adminOnly` templates.
  const isAdmin = useHasPermission('ADMIN');
  const templates = availableDriverNotificationTemplates({ isAdmin });

  const { preview, send, history } = useSelector((state) => state.notifications);

  // The admin cities query returns every city regardless of status; a driver
  // broadcast should only ever reference live markets, so offer operational
  // cities only in every picker on this page (cohort + route from/to).
  const { cities: operationalCities } = useCities({ operationalOnly: true });

  // Resolve a broadcast's cohort city id to a name for the log. Built from ALL
  // cities (not just operational), so a past send still names its city even if
  // that market was later turned off.
  const { cities: allCities } = useCities();
  const cityNameById = Object.fromEntries(allCities.map((c) => [c.id, cityLabel(c)]));

  const [city, setCity] = useState(null);
  const [template, setTemplate] = useState(templates[0]);
  const [params, setParams] = useState({});
  // Whether the trip is today or tomorrow — a global choice that applies to
  // every template, so it persists across template switches.
  const [when, setWhen] = useState(TripDayOptions[0].key);
  // Which drivers the broadcast targets (see `DriverSegmentOptions`). Like trip
  // timing, this is a global choice applied to every template, so it persists
  // across template switches. Defaults to non-XL drivers, as before.
  const [segmentKey, setSegmentKey] = useState(DriverSegmentOptions[0].key);
  const segment = getDriverSegment(segmentKey);
  // Copy typed for the "Custom message" template. Kept across template switches
  // so a mis-click doesn't lose a drafted message; cleared after a send.
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    // Paint the log instantly from the last cached response, then reconcile
    // with the live server history.
    hydrateHistoryCache(readHistoryCache())(dispatch);
    fetchBroadcastHistory()(dispatch);
    return () => resetBroadcast()(dispatch);
  }, [dispatch]);

  // Keep the instant-paint cache in step with the loaded server history —
  // confirmed server rows only. Optimistic `_local` rows are never cached, so a
  // failed refetch can't persist an unconfirmed send that later re-hydrates with
  // no server row to supersede it.
  useEffect(() => {
    if (history.loaded) {
      writeHistoryCache(history.items.filter((b) => !b._local));
    }
  }, [history.loaded, history.items]);

  // Refresh the recipient count whenever the city or the driver segment
  // changes. The cohort is city + driver status (+ the XL segment) — the
  // backend doesn't filter by template.
  useEffect(() => {
    if (city) {
      fetchBroadcastPreview(city.id, getDriverSegment(segmentKey).xlOnlyFlags)(dispatch);
    }
  }, [city, segmentKey, dispatch]);

  const selectTemplate = (t) => {
    setTemplate(t);
    setParams({});
  };

  // Route fields hold a city object from the picker; only the city's name is
  // interpolated into the push copy and sent along as data.
  const onParamChange = (name) => (selected) => setParams((prev) => ({ ...prev, [name]: selected }));
  const previewParams = {
    ...Object.fromEntries(template.fields.map((f) => [f.name, params[f.name] ? params[f.name].cityName : ''])),
    when,
  };

  const onSend = async () => {
    // Trip timing only feeds the fixed templates' copy; a custom message has
    // none, so it isn't sent or logged.
    const sendWhen = template.custom ? undefined : when;
    const result = await sendBroadcast({
      homeCity: city.id,
      title: pushTitle,
      body: pushBody,
      event: template.event,
      origin: previewParams.origin,
      destination: previewParams.destination,
      when: sendWhen,
      xlOnlyFlags: segment.xlOnlyFlags,
    })(dispatch);
    setConfirmOpen(false);

    // Show every segment that went out in the log immediately — including the
    // delivered half of a partial send — then refetch so the server rows (with
    // their real ids) supersede these optimistic ones.
    const sentAt = new Date().toISOString();
    result.sent.forEach((s, i) =>
      recordLocalBroadcast({
        id: `local-${Date.now()}-${i}`,
        homeCity: city.id,
        title: pushTitle,
        body: pushBody,
        event: template.event,
        origin: previewParams.origin || null,
        destination: previewParams.destination || null,
        when: sendWhen || null,
        xlOnly: s.xlOnly,
        tripId: null,
        recipientCount: s.queued,
        sentAt,
        _local: true,
      })(dispatch),
    );
    if (result.sent.length > 0) {
      fetchBroadcastHistory()(dispatch);
    }

    const queued = result.sent.reduce((sum, s) => sum + s.queued, 0);
    if (result.success) {
      toast.success(`Notification queued for ${queued} drivers in ${cityLabel(city)}.`);
      setParams({});
      if (template.custom) {
        setCustomTitle('');
        setCustomBody('');
      }
    } else if (result.sent.length > 0) {
      // Some drivers already have the push. Say so plainly, so nobody resends
      // to "All drivers" and notifies the delivered segment twice.
      const delivered = result.sent.map((s) => xlSegmentName(s.xlOnly)).join(' and ');
      toast.error(
        `Sent to ${queued} ${delivered} drivers, but the ${xlSegmentName(result.failedXlOnly)} send failed: ${
          result.error
        }. Retry with "${result.failedXlOnly ? 'XL only' : 'Non-XL'}" only.`,
      );
    } else {
      toast.error(result.error || 'Failed to send notification.');
    }
  };

  if (!canSend) {
    return (
      <Box sx={flushPageSx}>
        <Paper elevation={0} sx={{ ...cardSx, flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Missing send_driver_notifications permission.</Typography>
        </Paper>
      </Box>
    );
  }

  // What actually goes out: the fixed template's title and generated copy, or
  // the typed title and message for a custom send.
  const pushTitle = template.custom ? customTitle.trim() : template.title;
  const pushBody = template.custom ? customBody.trim() : template.buildPreview(previewParams);
  const messagePreview = template.custom ? template.buildPreview({ body: pushBody }) : pushBody;
  const requiredFieldsFilled = template.custom
    ? !!pushTitle && !!pushBody
    : template.fields.every((f) => params[f.name]);
  const recipientCount = preview.count;
  // `templates` already hides admin-only ones; this keeps the send itself
  // gated too, so a non-ADMIN can never send one even if it were selected.
  const templateAllowed = !template.adminOnly || isAdmin;
  const canPreviewSend =
    templateAllowed && !!city && requiredFieldsFilled && !preview.loading && !preview.error && recipientCount > 0;

  return (
    <Box sx={flushPageSx}>
      {/* Sub-nav card — the fixed set of templates */}
      <Paper elevation={0} sx={{ ...cardSx, width: { xs: '100%', md: 308 }, flexShrink: 0, maxHeight: { xs: 320, md: 'none' } }}>
        <Box sx={{ p: '16px', pb: '12px', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Notification templates</Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, px: '8px', pb: '8px', ...scrollY }}>
          <List disablePadding>
            {templates.map((t) => (
              <TemplateListItem
                key={t.value}
                template={t}
                selected={t.value === template.value}
                onClick={() => selectTemplate(t)}
              />
            ))}
          </List>
        </Box>
      </Paper>

      {/* Content card — compose + send */}
      <Paper elevation={0} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
        <Box sx={cardHeaderSx}>
          <Stack spacing="2px">
            <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em' }}>{template.label}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
              {template.description}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ ...cardBodySx, ...scrollY }}>
          <Box sx={{ maxWidth: 560, width: '100%' }}>
            <Stack spacing="24px">
              <Box>
                <SectionLabel>Send to drivers in</SectionLabel>
                <FilterSelect
                  icon={<StationIcon />}
                  label="City"
                  placeholder="Choose a city"
                  value={city}
                  getOptionLabel={cityLabel}
                  getOptionKey={(c) => c.id}
                  options={operationalCities}
                  onChange={setCity}
                  clearLabel="Clear selection"
                  width={320}
                />
              </Box>

              {!template.custom && (
                <Box>
                  <SectionLabel>Trip timing</SectionLabel>
                  <Box sx={{ maxWidth: 320 }}>
                    <SegmentedFilter
                      value={when}
                      onChange={setWhen}
                      segments={TripDayOptions.map((o) => ({ key: o.key, label: o.label }))}
                    />
                  </Box>
                </Box>
              )}

              <Box>
                <SectionLabel>Drivers</SectionLabel>
                <Box sx={{ maxWidth: 360 }}>
                  <SegmentedFilter
                    value={segmentKey}
                    onChange={setSegmentKey}
                    segments={DriverSegmentOptions.map((o) => ({ key: o.key, label: o.label }))}
                  />
                </Box>
                <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mt: '8px' }}>
                  {segmentKey === 'xl'
                    ? 'Only drivers tagged XL will get this.'
                    : segmentKey === 'all'
                    ? 'Every driver gets this. Sent as two pushes, non-XL then XL.'
                    : 'Only drivers without the XL tag will get this.'}
                </Typography>
              </Box>

              {template.custom && (
                <Stack spacing="16px">
                  <Box>
                    <SectionLabel>Title</SectionLabel>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. Earn $20 today"
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      inputProps={{ 'aria-label': 'Notification title', maxLength: 65 }}
                    />
                  </Box>
                  <Box>
                    <SectionLabel>Message</SectionLabel>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      size="small"
                      placeholder="What drivers will read in the notification"
                      value={customBody}
                      onChange={(e) => setCustomBody(e.target.value)}
                      inputProps={{ 'aria-label': 'Notification message', maxLength: 240 }}
                    />
                  </Box>
                </Stack>
              )}

              {template.fields.length > 0 && (
                <Box sx={routeFieldsSx}>
                  {template.fields.map((f) => (
                    <Box key={f.name} sx={routeFieldSx}>
                      <SectionLabel>{f.label}</SectionLabel>
                      <FilterSelect
                        icon={<StationIcon />}
                        label={f.pillLabel || f.label}
                        placeholder="Choose a city"
                        value={params[f.name] || null}
                        getOptionLabel={cityLabel}
                        getOptionKey={(c) => c.id}
                        options={operationalCities}
                        onChange={onParamChange(f.name)}
                        clearLabel="Clear selection"
                        width={320}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <Box>
                <SectionLabel>Message preview</SectionLabel>
                <Box
                  sx={{
                    borderRadius: '10px',
                    backgroundColor: softFill,
                    px: '14px',
                    py: '12px',
                    fontSize: '14px',
                    lineHeight: '22px',
                    fontStyle: 'italic',
                    color: 'text.primary',
                  }}
                >
                  {template.custom && (
                    <Box component="span" sx={{ display: 'block', fontStyle: 'normal', fontWeight: 600 }}>
                      {pushTitle || '[title]'}
                    </Box>
                  )}
                  “{messagePreview}”
                </Box>
              </Box>

              {city && (
                <Box>
                  {preview.loading && <Skeleton variant="text" width={240} height={22} />}
                  {preview.error && (
                    <Typography sx={{ fontSize: '13px', color: 'error.main' }}>
                      Couldn’t load recipient count: {preview.error}
                    </Typography>
                  )}
                  {!preview.loading && !preview.error && recipientCount != null && (
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                      This will notify {describeAudience(recipientCount, segmentKey, city)}.
                    </Typography>
                  )}
                </Box>
              )}

              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!canPreviewSend || send.loading}
                  onClick={() => setConfirmOpen(true)}
                  sx={{
                    borderRadius: '10px',
                    py: '10px',
                    fontWeight: 600,
                    fontSize: '15px',
                    px: '32px',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover:not(:disabled)': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  Send notification
                </Button>
              </Box>
            </Stack>
          </Box>

          {/* Sent-log — the record of what actually went out, divided from the
              compose form by a hairline. */}
          <Box
            sx={{
              maxWidth: 560,
              width: '100%',
              mt: '28px',
              pt: '24px',
              borderTop: (theme) => `1px solid ${hairline(theme)}`,
            }}
          >
            <SentLog
              items={history.items}
              cityNameById={cityNameById}
              loading={history.loading}
              error={history.error}
            />
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={() => !send.loading && setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle>Send notification?</DialogTitle>
        <DialogContent sx={{ maxWidth: '400px' }}>
          <Typography sx={{ marginBottom: '12px' }}>
            Send to <strong>{describeAudience(recipientCount, segmentKey, city)}</strong>? This cannot be undone.
          </Typography>
          {template.custom && <Typography sx={{ fontWeight: 600 }}>{pushTitle}</Typography>}
          <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>“{messagePreview}”</Typography>
        </DialogContent>
        <DialogActions sx={{ px: '24px', pb: '20px' }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={send.loading} sx={{ borderRadius: '8px' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSend}
            loading={send.loading}
            sx={{ borderRadius: '8px' }}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverNotifications;
